import { Outlet, NavLink, Navigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  HardDrive,
  CalendarDays,
  BarChart3,
  Database,
  FileSearch,
  LogOut,
  UserCircle2,
  Bell,
  Stethoscope,
  FolderOpen,
  FileText,
  Video,
  Handshake,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useSessionBootstrap } from '@/hooks/useSessionBootstrap';
import { logout as apiLogout } from '@/lib/api';
import { useNotifications } from '@/hooks/useClinical';
import { useNotificationHub } from '@/hooks/useNotificationHub';
import { cn } from '@/lib/cn';
import { LanguageSwitcher } from '@/components/site/LanguageSwitcher';
import { ClienteSwitcher } from '@/components/ClienteSwitcher';
import { AvisosApp } from '@/components/AvisosApp';
import { DoisFatoresPainel } from '@/components/seguranca/DoisFatoresPainel';
import { TrocaSenhaObrigatoria } from '@/components/seguranca/TrocaSenhaObrigatoria';
import { useRotulos } from '@/i18n/rotulos';

type NavItem = {
  to: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
};

const navClinico: NavItem[] = [
  { to: '/app/medico', labelKey: 'nav.minhasCirurgias', icon: LayoutDashboard, end: true },
  { to: '/app/agenda', labelKey: 'nav.agenda', icon: CalendarDays },
  { to: '/app/pacientes', labelKey: 'nav.pacientes', icon: Stethoscope },
  { to: '/app/gravacoes', labelKey: 'nav.gravacoes', icon: Video },
  { to: '/app/midia', labelKey: 'nav.biblioteca', icon: FolderOpen },
  { to: '/app/laudos', labelKey: 'nav.laudos', icon: FileText },
];

const navAdmin: NavItem[] = [
  { to: '/app/admin', labelKey: 'nav.adminHome', icon: LayoutDashboard, end: true },
  { to: '/app/admin/usuarios', labelKey: 'nav.usuarios', icon: Users },
  { to: '/app/admin/hospitais', labelKey: 'nav.hospitais', icon: Building2 },
  { to: '/app/admin/parceiros', labelKey: 'nav.parceiros', icon: Handshake },
  { to: '/app/admin/dispositivos', labelKey: 'nav.dispositivos', icon: HardDrive },
  { to: '/app/admin/cirurgias', labelKey: 'nav.cirurgias', icon: CalendarDays },
  { to: '/app/admin/relatorios', labelKey: 'nav.relatorios', icon: BarChart3 },
  { to: '/app/admin/storage', labelKey: 'nav.storage', icon: Database },
  { to: '/app/admin/auditoria', labelKey: 'nav.auditoria', icon: FileSearch },
];

export function AppLayout() {
  const rotulos = useRotulos();
  const ready = useSessionBootstrap();
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const { data: notifs } = useNotifications(true);
  const naoLidas = notifs?.length ?? 0;
  const { t } = useTranslation('app');
  // Antes dos returns condicionais (regra dos hooks). Sem usuário o hook não conecta.
  useNotificationHub(user?.id);

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-vyxora-bg-dark text-vyxora-text-light/60">
        {t('comum.carregando')}
      </div>
    );
  }

  if (!user) {
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?from=${from}`} replace />;
  }

  const isAdmin = user.papel === 'HospitalAdmin' || user.papel === 'GlobalAdmin';
  // ConsultantRemote entra junto do Surgeon: ele participa de cirurgias e precisa das
  // mesmas telas clínicas. Sem isso a sidebar dele fica completamente vazia.
  const isClinico = user.papel === 'Surgeon' || user.papel === 'ConsultantRemote';

  const sections: { titleKey: string; items: NavItem[] }[] = [];
  if (isClinico || isAdmin) sections.push({ titleKey: 'nav.clinicoSection', items: navClinico });
  if (isAdmin) sections.push({ titleKey: 'nav.adminSection', items: navAdmin });

  async function handleLogout() {
    const rt = useAuthStore.getState().refreshToken;
    if (rt) await apiLogout(rt).catch(() => {});
    useAuthStore.getState().clear();
  }

  return (
    <div className="flex min-h-screen bg-vyxora-bg-dark text-vyxora-text-light">
      <aside className="hidden w-64 flex-col border-r border-white/5 bg-black/30 md:flex">
        <div className="flex items-center border-b border-white/5 px-6 py-5">
          <img
            src="/imagens/logo_principal.png"
            alt="VYXORA"
            className="h-8 w-auto"
          />
        </div>

        <nav className="flex-1 overflow-y-auto p-3 text-sm">
          {sections.map((section) => (
            <div key={section.titleKey} className="mb-6">
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-vyxora-text-light/40">
                {t(section.titleKey)}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2 rounded px-3 py-2 transition',
                          isActive
                            ? 'bg-vyxora-cyan-400/15 text-vyxora-cyan-300'
                            : 'text-vyxora-text-light/70 hover:bg-white/5 hover:text-vyxora-text-light',
                        )
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {t(item.labelKey)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-white/5 p-3">
          <NavLink
            to="/app/perfil"
            className="mb-2 flex items-center gap-2 rounded px-3 py-2 text-sm text-vyxora-text-light/70 hover:bg-white/5"
          >
            <UserCircle2 className="h-4 w-4" />
            {t('nav.perfil')}
          </NavLink>
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-vyxora-text-light/70 hover:bg-white/5"
          >
            <LogOut className="h-4 w-4" />
            {t('nav.sair')}
          </button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-white/5 px-6 py-3">
          <h1 className="text-sm text-vyxora-text-light/70">{t('header.platform')}</h1>
          <div className="flex items-center gap-4">
            <ClienteSwitcher />
            <LanguageSwitcher compact />
            <Link
              to="/app/notificacoes"
              className="relative rounded-full p-2 hover:bg-white/5"
              aria-label={t('nav.notificacoes')}
            >
              <Bell className="h-5 w-5" />
              {naoLidas > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {naoLidas > 9 ? '9+' : naoLidas}
                </span>
              )}
            </Link>
            <div className="text-sm">
              {user.nome}{' '}
              <span className="text-vyxora-text-light/50">({rotulos.papel(user.papel)})</span>
            </div>
          </div>
        </header>
        <AvisosApp />
        <main className="flex-1 overflow-x-hidden p-6">
          {user.deveTrocarSenha ? (
            // Vem antes do 2FA: a API recusa tudo com PASSWORD_CHANGE_REQUIRED, inclusive os
            // endpoints de configuração do segundo fator.
            <TrocaSenhaObrigatoria onSair={handleLogout} />
          ) : user.doisFAObrigatorio && !user.sessaoComMfa ? (
            <DoisFAObrigatorio doisFAAtivo={user.doisFAAtivo} onSair={handleLogout} />
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}

/**
 * Administrador sem 2FA na sessão: a API recusa tudo fora da configuração
 * (TWO_FACTOR_SETUP_REQUIRED), então a tela também. Quem já tem 2FA ligado, mas está numa sessão
 * antiga (sem o segundo fator), só precisa entrar de novo.
 */
function DoisFAObrigatorio({ doisFAAtivo, onSair }: { doisFAAtivo: boolean; onSair: () => void }) {
  const { t } = useTranslation('app');
  return (
    <div className="mx-auto max-w-2xl">
      <section className="card">
        <h1 className="font-display text-xl font-semibold">{t('doisFAObrigatorio.titulo')}</h1>
        <div className="mt-4">
          {doisFAAtivo ? (
            <div className="space-y-4 text-sm text-vyxora-text-light/80">
              <p>{t('doisFAObrigatorio.sessaoSemMfa')}</p>
              <button onClick={onSair} className="btn-primary text-sm">{t('doisFAObrigatorio.entrarNovamente')}</button>
            </div>
          ) : (
            <DoisFatoresPainel obrigatorio />
          )}
        </div>
      </section>
    </div>
  );
}
